import { ArrowRight } from 'lucide-react';
import cn from 'classnames';
import { setOAuthRedirectFlag } from '@/utils/auth';

interface GoogleAuthButtonProps {
  /** 按钮文字 */
  text?: string;
  /** Google OAuth 跳转地址 */
  href?: string;
  /** 是否禁用 */
  disabled?: boolean;
  /** 自定义类名 */
  className?: string;
}

/**
 * Google 图标组件
 */
function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M18.1711 8.36792H17.4998V8.33325H9.99984V11.6666H14.7095C14.0223 13.6071 12.1761 14.9999 9.99984 14.9999C7.23859 14.9999 4.99984 12.7612 4.99984 9.99992C4.99984 7.23867 7.23859 4.99992 9.99984 4.99992C11.2745 4.99992 12.4341 5.48075 13.3173 6.26617L15.6745 3.909C14.1858 2.52192 12.1948 1.66659 9.99984 1.66659C5.39775 1.66659 1.6665 5.39784 1.6665 9.99992C1.6665 14.602 5.39775 18.3333 9.99984 18.3333C14.6019 18.3333 18.3332 14.602 18.3332 9.99992C18.3332 9.44117 18.2757 8.89575 18.1711 8.36792Z"
        fill="#FFC107"
      />
      <path
        d="M2.62744 6.12117L5.36536 8.12909C6.10619 6.295 7.90036 4.99992 9.99994 4.99992C11.2746 4.99992 12.4341 5.48075 13.3174 6.26617L15.6746 3.909C14.1858 2.52192 12.1949 1.66659 9.99994 1.66659C6.79911 1.66659 4.02327 3.47375 2.62744 6.12117Z"
        fill="#FF3D00"
      />
      <path
        d="M10.0002 18.3333C12.1527 18.3333 14.1085 17.5095 15.5873 16.17L13.0085 13.9875C12.1435 14.6452 11.0865 15.0008 10.0002 14.9999C7.83274 14.9999 5.99232 13.6179 5.29899 11.6892L2.58149 13.7829C3.96066 16.4817 6.76149 18.3333 10.0002 18.3333Z"
        fill="#4CAF50"
      />
      <path
        d="M18.1713 8.36803H17.5V8.33337H10V11.6667H14.7096C14.3809 12.5902 13.7889 13.3972 13.0067 13.988L13.0079 13.9871L15.5867 16.1696C15.4046 16.3355 18.3333 14.1667 18.3333 10C18.3333 9.44128 18.2758 8.89587 18.1713 8.36803Z"
        fill="#1976D2"
      />
    </svg>
  );
}

/** Popup 窗口尺寸 */
const POPUP_WIDTH = 500;
const POPUP_HEIGHT = 600;

/**
 * Google 认证按钮
 * 使用独立小弹窗打开 OAuth，带 mode=popup 参数，回调后自动关闭
 */
export function GoogleAuthButton({
  text = 'Sign up with Google',
  href,
  disabled = false,
  className,
}: GoogleAuthButtonProps) {
  const handleClick = (e: React.MouseEvent) => {
    if (href && !disabled) {
      e.preventDefault();
      setOAuthRedirectFlag();

      const popupUrl = `${href}${href.includes('?') ? '&' : '?'}mode=popup`;
      const left = (window.screen.width - POPUP_WIDTH) / 2;
      const top = (window.screen.height - POPUP_HEIGHT) / 2;
      const features = [
        `width=${POPUP_WIDTH}`,
        `height=${POPUP_HEIGHT}`,
        `left=${left}`,
        `top=${top}`,
        'menubar=no',
        'toolbar=no',
        'status=no',
        'location=no',
        'scrollbars=yes',
        'resizable=yes',
      ].join(',');

      window.open(popupUrl, 'google_oauth_popup', features);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={cn(
        'flex w-full items-center justify-center gap-[8px] rounded-[8px] border border-[#e5e5e5] bg-white px-[24px] py-[9.5px]',
        'min-h-[40px] shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_0px_rgba(0,0,0,0.1)]',
        'transition-colors hover:bg-gray-50',
        'cursor-pointer',
        disabled && 'pointer-events-none opacity-50',
        className
      )}
    >
      <GoogleIcon className="shrink-0" />
      <span className="flex-1 text-center text-[14px] font-medium leading-[1.5] tracking-[0.07px] text-[#171717]">
        {text}
      </span>
      <ArrowRight className="h-[20px] w-[20px] shrink-0 text-black" />
    </button>
  );
}

export default GoogleAuthButton;
